// ══════════════ STAFF UI — optimisé mobile ══════════════
// Extrait de GerantClientUI.jsx (refacto étape StaffUI, 08/07/2026).
// Feuille : les 11 briques terrain (GeoHelpBanner, DailyPunch, AptPunch,
// RepLibreSelector, ControleLibreSelector, LeaveForm, CommentaireModal,
// AlerteSanteButton, CaniBandeau, CaniHydrationBanner, CaniTab) sont maintenant
// toutes importées depuis leurs vraies sources — contrôle anti-cycle exhaustif
// confirmé : aucun composant référencé n'est encore défini dans GerantClientUI.
import React, { useState, useEffect } from 'react';
import { today, now, fname, leaveHistoryEntry, fd, guideFreedom, calcFinJourneeAptAnomalies } from "../utils.js";
import { TT } from "../constants.js";
import { notifEventBus } from '../notifBus.js';
import { supaGet, supaSet, supaSetStrict } from "../supabase.js";
import { Ic } from "./Ic.jsx";
import { M } from "./M.jsx";
import { WeekPlan } from "./TaskForm.jsx";
import { FichePrepaModal } from "./SecPrest.jsx";
import { SecControles, CtrlModal } from "./SecRapports.jsx";
import { getRapportStructure, ErrorBoundary, RapportModal } from './ErrorBoundary.jsx';
import { ModeGuide } from './ModeGuide.jsx';
import { SimpleChat } from './SimpleChat.jsx';
import { OTQuick } from './OTQuick.jsx';
import { GeoHelpBanner } from './GeoHelpBanner.jsx';
import { DailyPunch, AptPunch } from './PointageTerrain.jsx';
import { RepLibreSelector, ControleLibreSelector } from './RapportSelectors.jsx';
import { LeaveForm, CommentaireModal, DureeSignalModal } from './RHTerrain.jsx';
import { AlerteSanteButton, CaniBandeau, CaniHydrationBanner, CaniTab, useCaniculeActif } from './Canicule.jsx';
import { AssistanceButton } from './EncadrementRenforce.jsx';
import { RgpdInfoModal } from './RgpdInfo.jsx';

function StaffUI({sess,...P}){
  const{st,biens,staff,gerants,leave,setLeave,msgs,setMsgs,tc,setTc,setTcSynced,als,setAls,setAlsSynced,rep,setRep,pj,prov,clients,objets,setObjets,setAssist,setDureeAnom,pauseQR,setPauseQR,rgpdDoc,rgpdAck,setRgpdAck,rgpdPending,setRgpdPending}=P;
  const me=staff.find(s=>s.id===sess.id);
  // Trace persistante d'une demande d'aide (encadrement renforcé, Lot A) : écriture
  // read-before-write sur assist7 pour ne pas écraser un « traité » posé en parallèle
  // côté Direction. Alimente le bandeau rouge admin/gérant + le journal SecRH.
  const onAssist=async(rec)=>{
    try{
      const base=await supaGet('assist7')||[];
      const merged=[...base,rec];
      await supaSet('assist7',merged);if(setAssist)setAssist(merged);
    }catch(e){console.error('[assist] écriture échouée :',e.message);if(setAssist)setAssist(as=>[...(as||[]),rec]);}
  };
  // Seuil qualité (v154, révisé 11/08/2026) : signalement d'empêchement à clôturer un
  // ménage hors durée min/max. `ctx` = {task,bien,sens,retry} posé par AptPunch au clic
  // sur ⏹ Fin quand hors fourchette (aucun chiffre transmis — juste le sens du
  // dépassement, pour le tri côté SecRH). Décision Claire du 11/08/2026 : l'envoi débloque
  // désormais directement la tâche (ctx.retry(), même fonction punch() que AptPunch,
  // rappelée avec skipDureeCheck) — ce n'est plus seulement une escalade qui attend un
  // déblocage manuel. Le motif reste obligatoire + engagement de véracité côté
  // DureeSignalModal (case à cocher, RHTerrain.jsx).
  const[dureeLockCtx,setDureeLockCtx]=useState(null);
  const onDureeSignal=async(ctx,motif,qualiteConfirmee)=>{
    const rec={id:'da'+Date.now(),sid:sess.id,tid:ctx.task.id,bid:ctx.bien?.id||null,bienName:ctx.bien?.name||'?',type:ctx.task.type,sens:ctx.sens,motif,fromName:me?.name||fname(me)||'Salarié(e)',ts:now(),traite:false,traitePar:null,traiteTs:null,qualiteConfirmee:ctx.sens==='sous'?!!qualiteConfirmee:undefined};
    try{
      notifEventBus.emit('duree_signalee',{fromName:rec.fromName,bienName:rec.bienName,sens:rec.sens,motif,summary:`⏱️ ${rec.fromName} signale un empêchement — ${rec.bienName}`});
    }catch(_){/* jamais bloquant */}
    try{
      const base=await supaGet('dureeAnom7')||[];
      const merged=[...base,rec];
      await supaSet('dureeAnom7',merged);if(setDureeAnom)setDureeAnom(merged);
    }catch(e){console.error('[dureeAnom] écriture échouée :',e.message);if(setDureeAnom)setDureeAnom(ds=>[...(ds||[]),rec]);}
    // Le déblocage de la tâche ne doit jamais dépendre du succès de l'écriture ci-dessus
    // (best-effort, même garantie que le reste de dureeAnom7).
    ctx.retry&&ctx.retry();
  };
  // Reprise silencieuse (v161, 15/08/2026, demande Claire) : le salarié ferme la modale
  // sans envoyer de signalement, sur un ménage trop rapide (`sens==='sous'` uniquement,
  // seul cas géré par DureeSignalModal — voir RHTerrain.jsx). Même table `dureeAnom7`,
  // distinguée par `geste:'reprise'` : pas de motif, `traite:true` d'office (ne
  // correspond à aucune action à prendre côté Direction, exclue du badge/liste actifs
  // dans SecRH.jsx), et surtout AUCUN `notifEventBus.emit` — c'est tout l'objet de la
  // demande, garder une trace sans alerter en temps réel à chaque tentative. La tâche
  // elle-même n'a rien à débloquer : `punch()` n'avait rien écrit dans `als7`, elle est
  // donc déjà "reprenable" par un nouveau tap sur ⏹ Fin.
  const onDureeReprise=async(ctx)=>{
    const rec={id:'da'+Date.now(),sid:sess.id,tid:ctx.task.id,bid:ctx.bien?.id||null,bienName:ctx.bien?.name||'?',type:ctx.task.type,sens:ctx.sens,geste:'reprise',motif:undefined,fromName:me?.name||fname(me)||'Salarié(e)',ts:now(),traite:true,traitePar:null,traiteTs:null,qualiteConfirmee:undefined};
    try{
      const base=await supaGet('dureeAnom7')||[];
      const merged=[...base,rec];
      await supaSet('dureeAnom7',merged);if(setDureeAnom)setDureeAnom(merged);
    }catch(e){console.error('[dureeAnom reprise] écriture échouée :',e.message);if(setDureeAnom)setDureeAnom(ds=>[...(ds||[]),rec]);}
  };
  const[tab,setTab]=useState('ptg');
  // Pilotage canicule réellement actif (niveau défini et ≠ 'vert') — masque l'onglet
  // 🌡️ Canicule + le rappel hydratation quand Claire/Franck ont désactivé le module.
  const caniculeActif=useCaniculeActif();
  useEffect(()=>{if(tab==='cnc'&&!caniculeActif)setTab('ptg');},[caniculeActif]);
  const[repT,setRepT]=useState(null);
  const[guideT,setGuideT]=useState(null); // tâche en cours en mode guidé formation
  const[commentT,setCommentT]=useState(null);
  const[showOT,setShowOT]=useState(false);
  const[otConfirm,setOtConfirm]=useState(null);
  const[fichePrepa,setFichePrepa]=useState(null);
  const[showRepLibre,setShowRepLibre]=useState(false);
  const[repLibreBid,setRepLibreBid]=useState('');
  const[repLibreDate,setRepLibreDate]=useState('');
  const[repLibreT,setRepLibreT]=useState(null);
  const[showCtrlLibre,setShowCtrlLibre]=useState(false);
  const[ctrlLibreBid,setCtrlLibreBid]=useState('');
  const[ctrlLibreRepId,setCtrlLibreRepId]=useState(null); // repId rattaché, null=orphelin
  const[ctrlLibreOpen,setCtrlLibreOpen]=useState(false);  // true=ouvrir CtrlModal
  const[ctrlLibreDirty,setCtrlLibreDirty]=useState(false);
  const closeCtrlLibre=()=>{
    if(ctrlLibreDirty&&!window.confirm('Ce contrôle a des modifications non enregistrées (photos, notes…). Fermer sans sauvegarder ?')) return;
    setCtrlLibreDirty(false);
    setCtrlLibreOpen(false);setCtrlLibreBid('');setCtrlLibreRepId(null);
  };
  const myT=st.filter(t=>t.sid===sess.id);
  // Encadrement renforcé (Lot C) : tâches du jour non terminées qui bloquent la
  // clôture de journée (commencées sans fin, ou ménage fini sans rapport soumis).
  const clotureBlockers=me?.encadrementRenforce?(()=>{
    const todayS=today();
    const gL=(tid,type)=>als.find(l=>l.tid===tid&&l.type===type);
    const isMen=type=>['full','quick','linge','correction'].includes(type);
    const hasRep=tid=>(rep||[]).some(r=>r.tid===tid&&r.sub);
    return myT.filter(t=>t.date===todayS).map(t=>{
      const ls=gL(t.id,'start'),le=gL(t.id,'end');
      const bn=(biens.find(b=>b.id===t.bid)?.name)||'?';
      if(ls&&!le)return{id:t.id,bienName:bn,reason:'fin'};
      if(isMen(t.type)&&le&&!hasRep(t.id)&&!t.noReport)return{id:t.id,bienName:bn,reason:'rapport'};
      return null;
    }).filter(Boolean);
  })():[];
  // Lot 2 (SPEC_file_locale_v2.md) : appartements du jour non pointés, pour les
  // salariés SANS encadrementRenforce (eux ont déjà Lot C via clotureBlockers
  // ci-dessus — pas de doublon). Combiné dans DailyPunch avec la vérification
  // (async) de la file locale du lot 1 pour les rapports non confirmés.
  const finJourneeAptAnomalies=me?.encadrementRenforce?[]:
    calcFinJourneeAptAnomalies(myT,als,today()).map(a=>({...a,bienName:(biens.find(b=>b.id===a.bid)?.name)||'?'}));
  // Écriture read-before-write sur journal7 (même pattern que onAssist ci-dessus) :
  // beaucoup de salariés peuvent taper « Fin de poste » à des heures proches, un
  // setJournal naïf risquerait un écrasement last-write-wins entre deux sessions.
  const onFinJourneeLog=async(entry)=>{
    try{
      const base=await supaGet('journal7')||[];
      const merged=[...base,entry];
      await supaSet('journal7',merged);if(P.setJournal)P.setJournal(merged);
    }catch(e){console.error('[fin journée log] écriture échouée :',e.message);if(P.setJournal)P.setJournal(js=>[...(js||[]),entry]);}
  };
  const myL=leave.filter(l=>l.sid===sess.id);
  const ur=msgs.filter(m=>m.tid===`staff:${sess.id}`&&!m.ru).length;
  const[showLF,setShowLF]=useState(false);
  const[editMyL,setEditMyL]=useState(null); // absence du salarié en cours de modification
  const exRep=repT?rep.find(r=>r.tid===repT.id):null;
  const persistRep=(r,fin)=>{
    // Mise à jour React locale immédiate (affichage instantané).
    setRep(prev=>{const i=prev.findIndex(x=>x.id===r.id);return i>=0?prev.map(x=>x.id===r.id?r:x):[...prev,r];});
    // ─── Read-before-write à la soumission définitive (Option B, correctif 06/09/2026) ─────
    // Sur un brouillon (fin=false), le state local suffit : pas de risque d'écrasement car
    // personne d'autre n'écrit rep7 en réponse à un brouillon.
    // Sur la soumission finale (fin=true), on relit rep7 en base et on fusionne par id.
    // Cela protège contre tout onglet admin ou migration qui aurait lu une version périmée
    // et serait en train d'écrire sa propre version en parallèle.
    if(fin){
      (async()=>{
        try{
          const fresh=(await supaGet('rep7'))||[];
          const i=fresh.findIndex(x=>x.id===r.id);
          const merged=i>=0?fresh.map(x=>x.id===r.id?r:x):[...fresh,r];
          await supaSetStrict('rep7',merged);
          // Synchroniser le state React avec ce qu'on vient d'écrire en base.
          setRep(merged);
        }catch(e){
          // Échec réseau : le state React local est déjà à jour (ci-dessus),
          // et useSt/trySave tentera de le persister via son propre retry.
          console.error('[persistRep] read-before-write soumission finale échoué (non bloquant, retry useSt en cours)',e);
        }
      })();
    }
    // ─── notif : rapport_soumis (uniquement à la soumission définitive) ───
    if(fin && r.sub){
      const bien=biens.find(b=>b.id===r.bid);
      const auteur=staff.find(s=>s.id===r.sid);
      notifEventBus.emit('rapport_soumis',{
        reportId:r.id, taskId:r.tid, bienName:bien?.name,
        fromName:auteur?.name||fname(auteur)||'?',
        summary:`Rapport soumis pour ${bien?.name||'?'} par ${auteur?.name||fname(auteur)||'?'} (à contrôler)`
      });
    }
  };
  const saveRep=(r,fin)=>{persistRep(r,fin);if(fin)setRepT(null);};
  // Mode guidé : même persistance + notif, mais la fermeture est gérée par
  // l'écran de fin de ModeGuide (via onClose) — pas de fermeture auto ici.
  const saveGuide=(r,fin)=>{persistRep(r,fin);};
  const saveComment=c=>{
    // ─── notif : anomalie_signalee (uniquement à la création + si niveau ≠ info) ───
    const isNew=!(P.commentaires||[]).some(x=>x.id===c.id);
    if(isNew && c.niveau && c.niveau!=='info'){
      const bien=biens.find(b=>b.id===c.bid);
      const auteur=staff.find(s=>s.id===c.sid);
      const niveauLabel=c.niveau==='incident'?'🔴 Incident':'⚠️ Point à noter';
      notifEventBus.emit('anomalie_signalee',{
        fromName:auteur?.name||fname(auteur)||'Salarié(e)',
        commentId:c.id, niveau:c.niveau, taskType:c.type,
        bienName:bien?.name,
        summary:`${niveauLabel} signalé(e) sur ${bien?.name||'?'} par ${auteur?.name||fname(auteur)||'?'}`,
        body:c.texte||''
      });
    }
    P.setCommentaires(prev=>{const i=prev.findIndex(x=>x.id===c.id);return i>=0?prev.map(x=>x.id===c.id?c:x):[...prev,c];});
    setCommentT(null);
  };
  const isGouvernante=me?.gouvernante;
  const ctrlEnAttente=(P.rep||[]).filter(r=>r.sub&&!(P.controles||[]).some(c=>c.repId===r.id&&(c.verdict==='pret'||c.verdict==='nonpret'))).length;
  return(<div style={{paddingBottom:100}}>
    {/* Consentement RGPD (SPEC_rgpd_consentement_v1.md) — indépendant de l'onglet actif,
        réapparaît à chaque ouverture de l'appli tant qu'aucun accusé n'existe pour la
        version courante de rgpdDoc7. Ne bloque jamais le pointage ni le reste de l'appli. */}
    <RgpdInfoModal sid={sess.id} me={me} rgpdDoc={rgpdDoc} rgpdAck={rgpdAck} setRgpdAck={setRgpdAck} rgpdPending={rgpdPending} setRgpdPending={setRgpdPending}/>
    <div className="card" style={{padding:'16px 18px',marginBottom:16,background:'linear-gradient(135deg,#e8f5ed,var(--wh))',borderLeft:'4px solid var(--mn)'}}><div className="D" style={{fontSize:24,marginBottom:2}}>Bonjour {me?.firstName||me?.name?.split(' ')[0]||''} 👋</div><div style={{fontSize:11,color:'var(--si)'}}>{me?.role}</div></div>
    {/* ── Bandeau feedback contrôle qualité v2 (SPEC_controle_menage_v2 §5) ── */}
    {(()=>{
      const feedbacks=((P.controles||[]).filter(c=>
        c.version===2&&
        !c.feedbackVu?.[sess.id]&&
        (c.verdict==='pret'||c.verdict==='nonpret')&&
        (P.rep||[]).some(r=>(c.repId?r.id===c.repId:r.bid===c.bid&&r.date===c.date)&&r.sid===sess.id)
      ));
      if(feedbacks.length===0)return null;
      return(<div style={{display:'flex',flexDirection:'column',gap:6,marginBottom:16}}>
        {feedbacks.map(c=>{
          const bien=(biens||[]).find(b=>b.id===c.bid);
          const nc=c.nonConformites||0;
          const tot=c.totalItemsRapport||0;
          const scoreNs=Object.values(c.pieces||{}).map(p=>p?.note).filter(Boolean);
          const avg=scoreNs.length?(scoreNs.reduce((s,n)=>s+n,0)/scoreNs.length).toFixed(1):null;
          const dateLabel=c.closedAt?new Date(c.closedAt).toLocaleDateString('fr-FR',{day:'2-digit',month:'2-digit'}):'';
          const hasProblems=nc>0||(avg&&parseFloat(avg)<3);
          return(<div key={c.id} style={{background:hasProblems?'#fde0e0':'#e1f7ee',border:`1.5px solid ${hasProblems?'var(--dg)':'var(--mn)'}`,borderRadius:10,padding:'10px 13px',cursor:'pointer'}} onClick={async()=>{
            const items=Object.entries(c.items||{}).filter(([_,v])=>v.ok===false);
            const struct=bien?getRapportStructure(bien):null;
            const allItems=struct?.items||[];
            const detail=items.length>0
              ?items.map(([id,v])=>{
                const found=allItems.find(it=>it.id===id);
                const lb=found?.lb||found?.l||id;
                return`• ${lb}${v.obs?' — '+v.obs:''}`;
              }).join('\n')
              :'Aucun point relevé.';
            window.alert(`Contrôle du ${dateLabel} — ${bien?.name||'?'}\n\nNote : ${avg||'—'}/4\nNon-conformités : ${nc} / ${tot}\n\n${detail}`);
            try{
              const base=await supaGet('controles7')||[];
              const updated=base.map(x=>x.id===c.id?{...x,feedbackVu:{...(x.feedbackVu||{}),[sess.id]:true}}:x);
              await supaSet('controles7',updated);
              P.setControles&&P.setControles(prev=>prev.map(x=>x.id===c.id?{...x,feedbackVu:{...(x.feedbackVu||{}),[sess.id]:true}}:x));
            }catch(e){console.error('[feedback] feedbackVu échoué',e);}
          }}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:8}}>
              <div style={{flex:1}}>
                <div style={{fontSize:12.5,fontWeight:700,color:hasProblems?'var(--dg)':'var(--ok)'}}>{hasProblems?'⚠️':'✅'} Contrôle du {dateLabel} — {bien?.name||'?'}</div>
                <div style={{fontSize:11,color:'var(--si)',marginTop:2}}>{nc} non-conformité{nc>1?'s':''}{avg?` · Note ${avg}/4`:''}</div>
              </div>
              <span style={{fontSize:11,color:'var(--si)',flexShrink:0}}>Voir →</span>
            </div>
          </div>);
        })}
      </div>);
    })()}
    <div style={{display:'flex',gap:3,flexWrap:'wrap',marginBottom:18}}>
      <button className={`st ${tab==='ptg'?'on':''}`} onClick={()=>setTab('ptg')}><Ic n="pn"/>Pointage</button>
      <button className={`st ${tab==='plan'?'on':''}`} onClick={()=>setTab('plan')}><Ic n="ca"/>Planning</button>
      <button className={`st ${tab==='prep'?'on':''}`} onClick={()=>setTab('prep')}>📋 Fiches apparts</button>
      <button className={`st ${tab==='cng'?'on':''}`} onClick={()=>setTab('cng')}><Ic n="lv"/>Congés</button>
      <button className={`st ${tab==='msg'?'on':''}`} onClick={()=>setTab('msg')}><Ic n="ch"/>Messagerie{ur>0&&<span className="bc">{ur}</span>}</button>
      {isGouvernante&&<button className={`st ${tab==='ctrl'?'on':''}`} onClick={()=>setTab('ctrl')}>🔍 Contrôles{ctrlEnAttente>0&&<span className="bc">{ctrlEnAttente}</span>}</button>}
      {caniculeActif&&<button className={`st ${tab==='cnc'?'on':''}`} onClick={()=>setTab('cnc')}>🌡️ Canicule</button>}
    </div>
    {/* Bouton flottant objets trouvés */}
    <div style={{position:'fixed',bottom:24,right:24,zIndex:40}}>
      <button onClick={()=>setShowOT(true)} className="btn bor" style={{boxShadow:'0 4px 14px rgba(255,134,23,.4)',padding:'12px 18px',fontSize:14}}>📦 Objet trouvé</button>
    </div>
    <AlerteSanteButton sid={sess.id} nom={me?.name||fname(me)} role={me?.role}/>
    {tab==='ptg'&&<div>{me?.encadrementRenforce&&<AssistanceButton me={me} gerants={gerants} onAssist={onAssist}/>}<GeoHelpBanner sid={sess.id} tc={tc} als={als}/><CaniBandeau/>{caniculeActif&&<CaniHydrationBanner tc={tc} sid={sess.id}/>}<DailyPunch sid={sess.id} tc={tc} setTc={setTc} setTcSynced={setTcSynced} encadre={!!me?.encadrementRenforce} langue={me?.langue||'fr'} clotureBlockers={clotureBlockers} authorName={me?.name||''} biens={biens} aptAnomalies={finJourneeAptAnomalies} onFinJourneeLog={onFinJourneeLog}/><div className="div"/><AptPunch sid={sess.id} st={myT} biens={biens} als={als} setAls={setAls} setAlsSynced={setAlsSynced} rep={rep} onRep={setRepT} onGuide={setGuideT} onComment={setCommentT} staff={staff} tc={tc} setTc={setTc} setTcSynced={setTcSynced} pauseQR={pauseQR} setPauseQR={setPauseQR} onDureeLock={setDureeLockCtx}/></div>}
    {tab==='plan'&&<WeekPlan st={st} pj={pj} staff={staff} prov={prov} biens={biens} leave={leave} arretsMaladie={(P.arretsMaladie||[]).filter(a=>a.sid===sess.id)} unavail={P.unavail} als={als} rep={rep} scopeS={sess.id}/>}
    {tab==='prep'&&(<div>
      <div className="D" style={{fontSize:18,marginBottom:4}}>Fiches de préparation</div>
      <div style={{fontSize:12,color:'var(--si)',marginBottom:16}}>Cliquez sur un appartement pour voir sa fiche de préparation.</div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))',gap:12}}>
        {biens.map(b=>{const pr=b.prep||{};const hasPrep=[pr.draps,pr.draps1p,pr.housses,pr.housses1p,pr.taies,pr.nespresso,pr.bretzels,pr.kitAccueilPrep,pr.packBienvenuePrep,pr.savonnettes,pr.shampooing,pr.gelDouche,pr.serviettes2p,pr.serviettes1p,...(pr.extras||[])].some(v=>v&&v!==0);return(<div key={b.id} className="card" style={{padding:'14px 16px',borderTop:'3px solid var(--mn)',cursor:'pointer'}} onClick={()=>setFichePrepa(b)}>
          <div className="D" style={{fontSize:15,marginBottom:3}}>{b.name}</div>
          <div style={{fontSize:11,color:'var(--si)',marginBottom:10}}>{b.addr}</div>
          {hasPrep?(<div style={{display:'flex',flexWrap:'wrap',gap:4}}>
            {(pr.draps||0)>0&&<span style={{background:'#e1f7ee',color:'#2d6b3e',fontSize:10,fontWeight:600,padding:'2px 7px',borderRadius:999}}>🛏 {pr.draps} draps</span>}
            {(pr.housses||0)>0&&<span style={{background:'#e1f7ee',color:'#2d6b3e',fontSize:10,fontWeight:600,padding:'2px 7px',borderRadius:999}}>🛌 {pr.housses} housses</span>}
            {(pr.nespresso||0)>0&&<span style={{background:'#fff4d6',color:'#7a5a00',fontSize:10,fontWeight:600,padding:'2px 7px',borderRadius:999}}>☕ {pr.nespresso} capsules</span>}
            {pr.bretzels&&<span style={{background:'#fff4d6',color:'#7a5a00',fontSize:10,fontWeight:600,padding:'2px 7px',borderRadius:999}}>🥨 Bretzels</span>}
            {pr.kitAccueilPrep&&<span style={{background:'var(--cr)',fontSize:10,fontWeight:600,padding:'2px 7px',borderRadius:999}}>🎁 Kit accueil</span>}
            {pr.packBienvenuePrep&&<span style={{background:'var(--cr)',fontSize:10,fontWeight:600,padding:'2px 7px',borderRadius:999}}>🥂 Pack bienvenue</span>}
          </div>):(<div style={{fontSize:11,color:'var(--si)',fontStyle:'italic'}}>Fiche non configurée</div>)}
          <div style={{marginTop:10,fontSize:11,fontWeight:700,color:'var(--mn)'}}>Voir la fiche →</div>
        </div>);})}
      </div>
      {fichePrepa&&<M onC={()=>setFichePrepa(null)} title={`📋 Fiche préparation · ${fichePrepa.name}`} col="var(--mn)" ch={<FichePrepaModal bien={fichePrepa} onClose={()=>setFichePrepa(null)}/>}/>}
    </div>)}
    {isGouvernante&&tab==='ctrl'&&(<div>
      <div style={{display:'flex',gap:8,marginBottom:16,flexWrap:'wrap'}}>
        <button onClick={()=>{setRepLibreBid(biens[0]?.id||'');setRepLibreDate(today());setRepLibreT(null);setShowRepLibre(true);}} className="btn bgh sm" style={{fontSize:12}}>🧹 Rapport libre</button>
        <button onClick={()=>{setCtrlLibreBid(biens[0]?.id||'');setCtrlLibreRepId(null);setCtrlLibreOpen(false);setShowCtrlLibre(true);}} className="btn bgh sm" style={{fontSize:12}}>🔍 Contrôle libre</button>
      </div>
      <SecControles controles={P.controles||[]} setControles={P.setControles} rep={rep} setRep={setRep} biens={biens} staff={staff} gerants={gerants} sess={sess} als={als}/>
    </div>)}
    {tab==='cng'&&(<div>
      <div style={{display:'flex',justifyContent:'space-between',marginBottom:12,flexWrap:'wrap',gap:8}}><div className="D" style={{fontSize:18}}>Mes congés</div><button onClick={()=>setShowLF(true)} className="btn bmn sm"><Ic n="plus"/>Demande</button></div>
      {myL.length===0?<div className="empty">Aucune demande</div>:<div style={{display:'flex',flexDirection:'column',gap:8}}>{myL.map(l=>{
        const isIndispo=l.type==='indispo';
        const isRepos=l.type==='repos';
        const isDemi=l.demi;
        return(<div key={l.id} className="card" style={{padding:'11px 14px',borderLeft:`3px solid ${isIndispo?'var(--or)':isRepos?'#6d28d9':isDemi?'var(--bl)':'var(--mn)'}`}}>
          <div style={{display:'flex',gap:5,marginBottom:4,flexWrap:'wrap'}}>
            <span className={`pill ${l.st==='pending'?'pnd':l.st==='approved'?'pok':'pdg'}`}>{l.st==='pending'?'En attente':l.st==='approved'?'Approuvé':'Refusé'}</span>
            {isIndispo&&<span style={{background:'#fff4d6',color:'var(--or)',fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:999}}>Indispo partielle</span>}
            {isRepos&&<span style={{background:'#ede9fe',color:'#6d28d9',fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:999}}>🔄 Repos hebdo</span>}
            {isDemi&&<span style={{background:'#e8f4ff',color:'#2266aa',fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:999}}>½ journée {l.demi==='am'?'Matin':'Après-midi'}</span>}
            {l.type==='recup'&&<span style={{background:'#f0faf3',color:'var(--ok)',fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:999}}>Récupération</span>}
          </div>
          <div className="D" style={{fontSize:14}}>
            {isIndispo||isRepos||isDemi?fd(l.sd):`du ${fd(l.sd)} au ${fd(l.ed)}`}
          </div>
          {isIndispo&&<div style={{fontSize:12,color:'var(--or)',fontWeight:600,marginTop:2}}>
            {l.indispoType==='creneau'?`Indispo de ${l.indispoStart} à ${l.indispoEnd}`:l.indispoType==='debut'?`Disponible à partir de ${l.indispoStart}`:`Disponible jusqu'à ${l.indispoEnd}`}
          </div>}
          {l.reason&&<div style={{fontSize:11,color:'var(--si)',marginTop:3}}>{l.reason}</div>}
          {(l.history&&l.history.length>0)&&<details style={{marginTop:7}}>
            <summary style={{fontSize:11,color:'var(--si)',cursor:'pointer'}}>🕓 Historique des modifications ({l.history.length})</summary>
            <div style={{display:'flex',flexDirection:'column',gap:4,marginTop:5,paddingLeft:4,borderLeft:'2px solid var(--li)'}}>
              {l.history.slice().reverse().map((h,i)=>(<div key={i} style={{fontSize:10,color:'var(--si)',lineHeight:1.4,paddingLeft:8}}>
                <span style={{color:'var(--ink)'}}>{new Date(h.at).toLocaleString('fr-FR',{day:'2-digit',month:'2-digit',year:'2-digit',hour:'2-digit',minute:'2-digit'})}</span> · {h.by} ({h.role})<br/>
                <span style={{textDecoration:'line-through',opacity:.7}}>{h.from}</span> → <span style={{color:'var(--ink)',fontWeight:600}}>{h.to}</span>
              </div>))}
            </div>
          </details>}
          <div style={{marginTop:8,display:'flex',justifyContent:'flex-end'}}>
            <button onClick={()=>setEditMyL(l)} className="btn bgh sm" title="Modifier ma demande">✏️ Modifier</button>
          </div>
        </div>);
      })}</div>}
      {showLF&&<M onC={()=>setShowLF(false)} title="Demande d'absence" col="var(--mn)" ch={<LeaveForm sid={sess.id} staff={staff} onSave={d=>{setLeave([...leave,d]);setShowLF(false);}} onC={()=>setShowLF(false)}/>}/>}
      {editMyL&&<M onC={()=>setEditMyL(null)} title="Modifier ma demande" col="var(--mn)" ch={
        <LeaveForm sid={sess.id} staff={staff} prefill={editMyL}
          warnResubmit={editMyL.st==='approved'}
          onSave={d=>{
            // Modification par le salarié : repasse en attente d'approbation + journal.
            const merged={...editMyL,...d};
            const entry=leaveHistoryEntry(editMyL,merged,me?fname(me):'Salarié','Salarié');
            const out={...merged,st:'pending',editedBy:me?fname(me):'Salarié',editedAt:now()};
            if(entry)out.history=[...(editMyL.history||[]),entry];
            setLeave(leave.map(x=>x.id===editMyL.id?out:x));
            setEditMyL(null);
          }}
          onC={()=>setEditMyL(null)}
        />
      }/>}
    </div>)}
    {tab==='msg'&&<SimpleChat sess={sess} msgs={msgs} setMsgs={setMsgs} accent="var(--mn)"/>}
    {tab==='cnc'&&caniculeActif&&<CaniTab tc={tc} sid={sess.id}/>}

    {/* ─── Rapport de ménage libre — sélecteur bien/date ─── */}
    {showRepLibre&&<RepLibreSelector
      biens={biens} st={st} rep={rep}
      bid={repLibreBid} setBid={setRepLibreBid}
      date={repLibreDate} setDate={setRepLibreDate}
      onConfirm={fakeTache=>{setRepLibreT(fakeTache);setShowRepLibre(false);}}
      onClose={()=>setShowRepLibre(false)}
    />}
    {repLibreT!==null&&(<M onC={()=>setRepLibreT(null)} title="🧹 Rapport de ménage" col="var(--or)" wide ch={
      <ErrorBoundary onClose={()=>setRepLibreT(null)}>
        <RapportModal
          task={repLibreT.id?repLibreT:null}
          bien={biens.find(b=>b.id===repLibreT.bid)||{id:repLibreT.bid}}
          sid={sess.id}
          existing={repLibreT.id?rep.find(r=>r.tid===repLibreT.id):null}
          onSave={saveRep}
          onClose={()=>setRepLibreT(null)}
          authorLabel={me?.name||''}
        />
      </ErrorBoundary>
    }/>)}

    {/* ─── Contrôle libre — sélecteur puis vrai CtrlModal ─── */}
    {showCtrlLibre&&<ControleLibreSelector
      biens={biens} rep={rep}
      bid={ctrlLibreBid} setBid={setCtrlLibreBid}
      onConfirm={(bid,repId)=>{setCtrlLibreBid(bid);setCtrlLibreRepId(repId);setCtrlLibreOpen(true);setShowCtrlLibre(false);}}
      onClose={()=>setShowCtrlLibre(false)}
    />}
    {ctrlLibreOpen&&ctrlLibreBid&&(<M
      onC={closeCtrlLibre}
      title="🔍 Contrôle appartement" col="var(--mn)" wide
      ch={<CtrlModal
        key={ctrlLibreRepId||ctrlLibreBid}
        repId={ctrlLibreRepId}
        bien={biens.find(b=>b.id===ctrlLibreBid)}
        controleurs={[...staff,...(gerants||[])]}
        existing={null}
        onSave={(ctrl,close)=>{
          P.setControles(prev=>[...(prev||[]),ctrl]);
          if(close){setCtrlLibreDirty(false);setCtrlLibreOpen(false);setCtrlLibreBid('');setCtrlLibreRepId(null);}
        }}
        onClose={closeCtrlLibre}
        onDirtyChange={setCtrlLibreDirty}
        sess={sess}
      />}
    />)}
    {repT&&<M onC={()=>setRepT(null)} title="Rapport de fin de ménage" col="var(--or)" wide ch={<ErrorBoundary onClose={()=>setRepT(null)}><RapportModal task={repT} bien={biens.find(b=>b.id===repT.bid)} sid={sess.id} existing={exRep} onSave={saveRep} onClose={()=>setRepT(null)} authorLabel={me?.name||''}/></ErrorBoundary>}/>}
    {guideT&&<div style={{position:'fixed',inset:0,zIndex:1000,background:'rgba(42,37,32,.45)',display:'flex',alignItems:'center',justifyContent:'center',padding:16}}><ErrorBoundary onClose={()=>setGuideT(null)}><ModeGuide task={guideT} bien={biens.find(b=>b.id===guideT.bid)} sid={sess.id} existing={rep.find(r=>r.tid===guideT.id)} onSave={saveGuide} onClose={()=>setGuideT(null)} mode={guideFreedom(me)} clients={clients} showTimer={!!me?.guideTimerOn} authorLabel={me?.name||''}/></ErrorBoundary></div>}
    {commentT&&<M onC={()=>setCommentT(null)} title={`💬 Commentaire · ${TT[commentT.type]?.l||''}`} col="var(--bl)" ch={<CommentaireModal task={commentT} bien={biens.find(b=>b.id===commentT.bid)} sid={sess.id} staff={staff} existing={P.commentaires?.find(c=>c.tid===commentT.id)} onSave={saveComment} onClose={()=>setCommentT(null)}/>}/>}
    {dureeLockCtx&&<M onC={()=>setDureeLockCtx(null)} title={`⏱️ Empêchement · ${dureeLockCtx.bien?.name||''}`} col="var(--or)" ch={<DureeSignalModal bien={dureeLockCtx.bien} sens={dureeLockCtx.sens} isStaff={true} onSend={(motif,qualiteConfirmee)=>onDureeSignal(dureeLockCtx,motif,qualiteConfirmee)} onClose={()=>setDureeLockCtx(null)} onReprise={()=>onDureeReprise(dureeLockCtx)}/>}/>}
    {showOT&&<M onC={()=>setShowOT(false)} title="📦 Déclarer un objet trouvé" col="var(--or)"
      ch={<OTQuick biens={biens} objets={P.objets||[]} setObjets={P.setObjets} otBacs={P.otBacs||[]} setOtBacs={P.setOtBacs} sejours={P.sejours||[]} statutAppts={P.statutAppts||{}} onClose={()=>{setShowOT(false);}}/>}/>}
    {otConfirm&&<M onC={()=>setOtConfirm(null)} title="Objet enregistré ✓" col="var(--ok)" ch={<div style={{textAlign:'center',padding:'8px 0'}}>
      <div style={{fontSize:48,marginBottom:12}}>📦</div>
      <div className="D" style={{fontSize:20,marginBottom:6,color:'var(--ok)'}}>Merci !</div>
      <div style={{fontSize:13,color:'var(--si)',marginBottom:16}}>L'objet a bien été enregistré dans le registre.</div>
      <div style={{background:'var(--cr)',borderRadius:12,padding:'14px',marginBottom:12}}>
        <div style={{fontSize:11,color:'var(--si)',marginBottom:4}}>Référence</div>
        <div className="D" style={{fontSize:22,color:'var(--or)'}}>{otConfirm.ref}</div>
        {otConfirm.casier&&<div style={{marginTop:8,fontSize:13,fontWeight:700}}>Ranger dans le <span style={{color:'var(--or)'}}>Casier {otConfirm.casier}</span></div>}
      </div>
      <div style={{fontSize:11,color:'var(--si)'}}>Notez cette référence sur l'étiquette de l'objet.</div>
      <button onClick={()=>setOtConfirm(null)} className="btn bor" style={{marginTop:16,width:'100%'}}>Fermer</button>
    </div>}/>}
  </div>);
}

export { StaffUI };

